import React, { useState, useCallback } from 'react';
import api from '../services/api';

const card = {
  border: '1px solid #cbd5e1',
  borderRadius: '12px',
  overflow: 'hidden',
  marginBottom: '24px',
  boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
  background: '#ffffff',
};
const header = {
  padding: '14px 20px',
  background: '#f1f5f9',
  borderBottom: '1px solid #e2e8f0',
  fontSize: '1.02rem',
  fontWeight: 600,
  color: '#334155',
};
const body = { padding: '20px' };
const tableStyle = {
  width: '100%',
  borderCollapse: 'collapse',
  fontSize: '0.88rem',
  marginTop: '12px',
};
const th = {
  border: '1px solid #ddd',
  padding: '8px',
  background: '#f4f4f4',
  textAlign: 'left',
};
const td = { border: '1px solid #ddd', padding: '8px' };
const btn = {
  padding: '8px 14px',
  marginRight: '8px',
  marginBottom: '8px',
  cursor: 'pointer',
  borderRadius: '6px',
  border: '1px solid #cbd5e1',
  background: '#fff',
};
const btnPrimary = { ...btn, background: '#0f172a', color: '#fff', borderColor: '#0f172a' };
const btnDanger = { ...btn, background: '#b91c1c', color: '#fff', borderColor: '#b91c1c' };

function CleanupTable({ rows, selectedIds, onToggle, onToggleAll }) {
  if (!rows.length) return <p style={{ color: '#64748b', margin: '8px 0 0' }}>미리보기 결과가 없습니다.</p>;
  const allChecked = rows.length > 0 && rows.every((r) => selectedIds.has(r.id));
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={tableStyle}>
        <thead>
          <tr>
            <th style={{ ...th, width: 40 }}>
              <input
                type="checkbox"
                checked={allChecked}
                onChange={(e) => onToggleAll(e.target.checked)}
              />
            </th>
            <th style={th}>ID</th>
            <th style={th}>참여자</th>
            <th style={th}>연·월</th>
            <th style={th}>사업</th>
            <th style={th}>사용(h)</th>
            <th style={th}>기지급(h)</th>
            <th style={th}>사유</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id}>
              <td style={td}>
                <input
                  type="checkbox"
                  checked={selectedIds.has(r.id)}
                  onChange={() => onToggle(r.id)}
                />
              </td>
              <td style={td}>{r.id}</td>
              <td style={td}>{r.participantName}</td>
              <td style={td}>
                {r.year}년 {r.month}월
              </td>
              <td style={td}>{r.projectName || '-'}</td>
              <td style={td}>{r.usedHoursSum}</td>
              <td style={td}>{r.payoutHours}</td>
              <td style={{ ...td, maxWidth: 220 }}>{r.reason}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** 참여기간·연도 불일치 연차 레코드 — 진단 전용(삭제 없음) */
function LeaveOutOfRangeDiagnosticTable({ rows }) {
  if (!rows.length) return <p style={{ color: '#64748b', margin: '8px 0 0' }}>미리보기 결과가 없습니다.</p>;
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={tableStyle}>
        <thead>
          <tr>
            <th style={th}>ID</th>
            <th style={th}>참여자</th>
            <th style={th}>참여연도</th>
            <th style={th}>시작일</th>
            <th style={th}>종료일</th>
            <th style={th}>레코드 연·월</th>
            <th style={th}>사업</th>
            <th style={th}>사용(h)</th>
            <th style={th}>기지급(h)</th>
            <th style={th}>판정 사유</th>
            <th style={{ ...th, minWidth: 200 }}>비고</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const hasVal =
              r.hasUsageOrPayout === true ||
              (parseFloat(r.usedHoursSum) || 0) !== 0 ||
              (parseFloat(r.payoutHours) || 0) !== 0;
            return (
              <tr
                key={r.id}
                style={
                  hasVal
                    ? { background: '#fff7ed' }
                    : undefined
                }
              >
                <td style={td}>{r.id}</td>
                <td style={td}>{r.participantName}</td>
                <td style={td}>{r.participantYear != null ? r.participantYear : '-'}</td>
                <td style={td}>{r.startDate || '-'}</td>
                <td style={td}>{r.endDate || '-'}</td>
                <td style={td}>
                  {r.year}년 {r.month}월
                </td>
                <td style={td}>{r.projectName || '-'}</td>
                <td style={td}>{r.usedHoursSum}</td>
                <td style={td}>{r.payoutHours}</td>
                <td style={{ ...td, maxWidth: 200 }}>{r.reason}</td>
                <td style={{ ...td, fontSize: '0.85rem' }}>
                  {hasVal
                    ? '연차 사용·기지급이 남아 있어 이 화면에서는 삭제하지 않습니다. 원인 확인 후 별도 조치하세요.'
                    : '사용·기지급이 모두 0이면 1번 메뉴에서 삭제할 수 있습니다.'}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function WorkCleanupTable({ rows, selectedIds, onToggle, onToggleAll }) {
  if (!rows.length) return <p style={{ color: '#64748b', margin: '8px 0 0' }}>미리보기 결과가 없습니다.</p>;
  const selectableIds = rows.map((r) => r.id);
  const allChecked =
    selectableIds.length > 0 && selectableIds.every((id) => selectedIds.has(id));
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={tableStyle}>
        <thead>
          <tr>
            <th style={{ ...th, width: 40 }}>
              <input
                type="checkbox"
                checked={allChecked}
                onChange={(e) => onToggleAll(e.target.checked)}
              />
            </th>
            <th style={th}>ID</th>
            <th style={th}>참여자</th>
            <th style={th}>연·월</th>
            <th style={th}>사업</th>
            <th style={th}>계획(h)</th>
            <th style={th}>상태</th>
            <th style={th}>사유</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
              <tr key={r.id}>
                <td style={td}>
                  <input
                    type="checkbox"
                    checked={selectedIds.has(r.id)}
                    onChange={() => onToggle(r.id)}
                  />
                </td>
                <td style={td}>{r.id}</td>
                <td style={td}>{r.participantName}</td>
                <td style={td}>
                  {r.year}년 {r.month}월
                </td>
                <td style={td}>{r.projectName || '-'}</td>
                <td style={td}>{r.plannedWorkHours}</td>
                <td style={td}>{r.status === 'closed' ? '마감' : '작성중'}</td>
                <td style={{ ...td, maxWidth: 260 }}>{r.reason}</td>
              </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const toYearParam = (y) => {
  const n = parseInt(y, 10);
  return n && Number.isFinite(n) ? `?year=${n}` : '';
};

const AdminDataCleanupPage = () => {
  const thisYear = String(new Date().getFullYear());

  const [outYear, setOutYear] = useState(thisYear);
  const [outItems, setOutItems] = useState([]);
  const [outLoading, setOutLoading] = useState(false);
  const [outMsg, setOutMsg] = useState('');

  const [emptyYear, setEmptyYear] = useState(thisYear);
  const [emptyItems, setEmptyItems] = useState([]);
  const [emptyLoading, setEmptyLoading] = useState(false);
  const [emptySelected, setEmptySelected] = useState(() => new Set());
  const [emptyMsg, setEmptyMsg] = useState('');

  const [workYear, setWorkYear] = useState(thisYear);
  const [workItems, setWorkItems] = useState([]);
  const [workLoading, setWorkLoading] = useState(false);
  const [workSelected, setWorkSelected] = useState(() => new Set());
  const [workMsg, setWorkMsg] = useState('');

  const [isolatedYear, setIsolatedYear] = useState(thisYear);
  const [isolatedItems, setIsolatedItems] = useState([]);
  const [isolatedLoading, setIsolatedLoading] = useState(false);
  const [isolatedSelected, setIsolatedSelected] = useState(() => new Set());
  const [isolatedMsg, setIsolatedMsg] = useState('');

  const [payoutYear, setPayoutYear] = useState(new Date().getFullYear());
  const [payoutPreview, setPayoutPreview] = useState(null);
  const [payoutLoading, setPayoutLoading] = useState(false);
  const [payoutMsg, setPayoutMsg] = useState('');

  const loadOutOfRange = useCallback(async () => {
    setOutLoading(true);
    setOutMsg('');
    try {
      const res = await api.get(`/admin/data-cleanup/leave-out-of-range/preview${toYearParam(outYear)}`);
      const items = res.data.items || [];
      setOutItems(items);
      const n = items.length;
      const withVal = items.filter(
        (r) =>
          r.hasUsageOrPayout === true ||
          (parseFloat(r.usedHoursSum) || 0) !== 0 ||
          (parseFloat(r.payoutHours) || 0) !== 0
      ).length;
      setOutMsg(
        `총 ${n}건 (연차 사용·기지급이 남은 행 ${withVal}건 — 삭제는 1번 메뉴에서 빈 레코드만)`
      );
    } catch (e) {
      setOutMsg(e.response?.data?.message || '조회 실패');
      setOutItems([]);
    } finally {
      setOutLoading(false);
    }
  }, [outYear]);

  const loadEmpty = useCallback(async () => {
    setEmptyLoading(true);
    setEmptyMsg('');
    try {
      const res = await api.get(`/admin/data-cleanup/empty-leave-records/preview${toYearParam(emptyYear)}`);
      setEmptyItems(res.data.items || []);
      setEmptySelected(new Set());
      setEmptyMsg(`총 ${res.data.count || 0}건`);
    } catch (e) {
      setEmptyMsg(e.response?.data?.message || '조회 실패');
      setEmptyItems([]);
    } finally {
      setEmptyLoading(false);
    }
  }, [emptyYear]);

  const loadIsolatedOpen = useCallback(async () => {
    setIsolatedLoading(true);
    setIsolatedMsg('');
    try {
      const res = await api.get(`/admin/data-cleanup/isolated-open-work/preview${toYearParam(isolatedYear)}`);
      setIsolatedItems(res.data.items || []);
      setIsolatedSelected(new Set());
      setIsolatedMsg(`총 ${res.data.count || 0}건`);
    } catch (e) {
      setIsolatedMsg(e.response?.data?.message || '조회 실패');
      setIsolatedItems([]);
    } finally {
      setIsolatedLoading(false);
    }
  }, [isolatedYear]);

  const loadWorkOutOfRange = useCallback(async () => {
    setWorkLoading(true);
    setWorkMsg('');
    try {
      const res = await api.get(`/admin/data-cleanup/work-out-of-range/preview${toYearParam(workYear)}`);
      setWorkItems(res.data.items || []);
      setWorkSelected(new Set());
      setWorkMsg(`총 ${res.data.count || 0}건`);
    } catch (e) {
      setWorkMsg(e.response?.data?.message || '조회 실패');
      setWorkItems([]);
    } finally {
      setWorkLoading(false);
    }
  }, [workYear]);

  const loadPayoutPreview = useCallback(async () => {
    setPayoutLoading(true);
    setPayoutMsg('');
    try {
      const res = await api.get('/admin/data-cleanup/payout-anomaly/preview', {
        params: { year: payoutYear },
      });
      setPayoutPreview(res.data);
      setPayoutMsg(
        `대상 참여자 ${res.data.participantCount || 0}명 (기지급 합계 ${res.data.totalPayoutHours ?? '-'}시간)`
      );
    } catch (e) {
      setPayoutMsg(e.response?.data?.message || '조회 실패');
      setPayoutPreview(null);
    } finally {
      setPayoutLoading(false);
    }
  }, [payoutYear]);

  const toggleEmpty = (id) => {
    setEmptySelected((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  };
  const toggleEmptyAll = (checked) => {
    if (!checked) {
      setEmptySelected(new Set());
      return;
    }
    setEmptySelected(new Set(emptyItems.map((r) => r.id)));
  };

  const toggleIsolated = (id) => {
    setIsolatedSelected((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  };
  const toggleIsolatedAll = (checked) => {
    if (!checked) { setIsolatedSelected(new Set()); return; }
    setIsolatedSelected(new Set(isolatedItems.map((r) => r.id)));
  };

  const runIsolatedClose = async () => {
    const ids = Array.from(isolatedSelected);
    if (ids.length === 0) { alert('마감할 항목을 선택하세요.'); return; }
    if (
      !window.confirm(
        `선택한 ${ids.length}건의 근무기록을 마감(closed) 처리합니다.\n총근무시간은 계획근무시간 - 결근 - 지각으로 자동 계산됩니다.\n계속하시겠습니까?`
      )
    ) return;
    if (!window.confirm('정말 마감 처리할까요?')) return;
    try {
      const res = await api.post('/admin/data-cleanup/isolated-open-work/execute', {
        ids,
        confirm: true,
      });
      alert(res.data.message || '처리되었습니다.');
      setIsolatedSelected(new Set());
      await loadIsolatedOpen();
    } catch (e) {
      alert(e.response?.data?.message || '실패');
    }
  };

  const toggleWork = (id) => {
    setWorkSelected((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  };
  const toggleWorkAll = (checked) => {
    if (!checked) {
      setWorkSelected(new Set());
      return;
    }
    setWorkSelected(new Set(workItems.map((r) => r.id)));
  };

  const runEmptyDelete = async () => {
    const ids = Array.from(emptySelected);
    if (ids.length === 0) {
      alert('삭제할 항목을 선택하세요.');
      return;
    }
    if (
      !window.confirm(
        `선택한 ${ids.length}건의 빈 연차 레코드(사용·기지급 0)를 삭제합니다.\n계속하시겠습니까?`
      )
    ) {
      return;
    }
    if (!window.confirm('정말 삭제할까요? 이 작업은 되돌릴 수 없습니다.')) return;
    try {
      const res = await api.post('/admin/data-cleanup/empty-leave-records/execute', {
        ids,
        confirm: true,
      });
      alert(res.data.message || '처리되었습니다.');
      setEmptySelected(new Set());
      await loadEmpty();
    } catch (e) {
      alert(e.response?.data?.message || '실패');
    }
  };

  const runWorkDelete = async () => {
    const ids = Array.from(workSelected);
    if (ids.length === 0) {
      alert('삭제할 항목을 선택하세요.');
      return;
    }
    const closedCount = workItems.filter(
      (r) => workSelected.has(r.id) && r.status === 'closed'
    ).length;
    if (
      !window.confirm(
        `선택한 ${ids.length}건의 근무기록(WorkRecord)을 삭제합니다.\n참여 시작·종료일과 맞지 않는 행만 서버에서 다시 검증 후 삭제됩니다.\n계속하시겠습니까?`
      )
    ) {
      return;
    }
    if (closedCount > 0) {
      if (
        !window.confirm(
          `선택한 항목 중 ${closedCount}건이 마감되어 있습니다.\n오류가 확실할 때만 마감된 근무기록을 삭제하세요.\n마감 건도 함께 삭제할까요?`
        )
      ) {
        return;
      }
    }
    if (!window.confirm('정말 삭제할까요? 이 작업은 되돌릴 수 없습니다.')) return;
    try {
      const res = await api.post('/admin/data-cleanup/work-out-of-range/execute', {
        ids,
        confirm: true,
      });
      alert(res.data.message || '처리되었습니다.');
      setWorkSelected(new Set());
      await loadWorkOutOfRange();
    } catch (e) {
      alert(e.response?.data?.message || '실패');
    }
  };

  const [cancelledYear, setCancelledYear] = useState(thisYear);
  const [cancelledPreview, setCancelledPreview] = useState(null);
  const [cancelledLoading, setCancelledLoading] = useState(false);
  const [cancelledRunLoading, setCancelledRunLoading] = useState(false);
  const [cancelledMsg, setCancelledMsg] = useState('');

  const loadCancelledPreview = useCallback(async () => {
    setCancelledLoading(true);
    setCancelledMsg('');
    setCancelledPreview(null);
    try {
      const res = await api.get('/leave/applications/fix-cancelled-records/preview', {
        params: cancelledYear.trim() ? { year: parseInt(cancelledYear, 10) } : {},
      });
      setCancelledPreview(res.data);
      setCancelledMsg(
        `취소 신청 스캔 ${res.data.scannedCount ?? 0}건 · 보정 대상 ${res.data.fixCount ?? 0}건`
      );
    } catch (e) {
      setCancelledMsg(e.response?.data?.message || '조회 실패');
      setCancelledPreview(null);
    } finally {
      setCancelledLoading(false);
    }
  }, [cancelledYear]);

  const runCancelledFix = async () => {
    if (!cancelledPreview || cancelledPreview.fixCount === 0) {
      alert('미리보기를 실행하고 보정 대상이 있을 때만 실행할 수 있습니다.');
      return;
    }
    if (
      !window.confirm(
        `총 ${cancelledPreview.fixCount}건의 연차 현황(usedLeaveDetails)에서 취소된 신청을 제거합니다. 진행할까요?`
      )
    ) {
      return;
    }
    if (!window.confirm('정말 보정을 실행할까요?')) return;
    setCancelledRunLoading(true);
    try {
      const body = cancelledYear.trim() ? { year: parseInt(cancelledYear, 10) } : {};
      const res = await api.post('/leave/applications/fix-cancelled-records', body);
      alert(res.data.message || '처리되었습니다.');
      setCancelledPreview(null);
      setCancelledMsg('');
      await loadCancelledPreview();
    } catch (e) {
      alert(e.response?.data?.message || '실패');
    } finally {
      setCancelledRunLoading(false);
    }
  };

  const runPayoutFix = async () => {
    if (!payoutPreview || !payoutPreview.items || payoutPreview.items.length === 0) {
      alert('먼저 미리보기를 실행하고 대상이 있을 때만 보정할 수 있습니다.');
      return;
    }
    if (
      !window.confirm(
        `${payoutYear}년도, 활동 중인데 기지급이 남아 있는 참여자 ${payoutPreview.participantCount}명의\n해당 연도 LeaveRecord 기지급(payoutHours)을 모두 0으로 초기화합니다.\n계속하시겠습니까?`
      )
    ) {
      return;
    }
    if (!window.confirm('정말 실행할까요?')) return;
    try {
      const res = await api.post('/admin/data-cleanup/payout-anomaly/execute', {
        year: payoutYear,
        confirm: true,
      });
      alert(res.data.message || '처리되었습니다.');
      await loadPayoutPreview();
    } catch (e) {
      alert(e.response?.data?.message || '실패');
    }
  };

  return (
    <div style={{ padding: '20px', maxWidth: 1100 }}>
      <h2 style={{ marginTop: 0 }}>전산데이터 정리</h2>
      <p style={{ color: '#64748b', fontSize: '0.95rem', lineHeight: 1.6, marginBottom: 20 }}>
        각 기능은 <strong>미리보기</strong>로 대상을 확인한 뒤, <strong>실행</strong> 시 한 번 더 확인합니다.
        삭제·보정은 되돌리기 어려우므로 신중히 사용하세요. <strong>2번</strong>은 연차 레코드 <strong>진단(조회)만</strong> 하며 실행 버튼이 없습니다.
      </p>

      {/* 1 — 빈 연차 삭제 (실행) */}
      <div style={card}>
        <div style={header}>1. 빈 연차 레코드 삭제 (참여기간 밖 · 사용·기지급 0)</div>
        <div style={body}>
          <p style={{ margin: '0 0 8px', color: '#475569', fontSize: '0.92rem' }}>
            <strong>참여기간·연도와 맞지 않는</strong> 월의 레코드 중, 연차 사용·기지급이 모두 0인 행만 표시합니다.
            참여 기간 안의 “아직 미사용 달”은 제외됩니다. 전수 목록은 <strong>2번</strong> 진단에서 확인할 수 있습니다.
          </p>
          <div style={{ marginBottom: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
            <label style={{ fontSize: '0.9rem' }}>
              연도
              <input
                type="text"
                inputMode="numeric"
                placeholder="예: 2025"
                value={emptyYear}
                onChange={(e) => setEmptyYear(e.target.value)}
                style={{ marginLeft: 6, padding: '4px 6px', width: 90 }}
              />
            </label>
            <span style={{ fontSize: '0.8rem', color: '#94a3b8' }}>비우면 전체</span>
          </div>
          <button type="button" style={btnPrimary} disabled={emptyLoading} onClick={loadEmpty}>
            {emptyLoading ? '조회 중…' : '미리보기'}
          </button>
          {emptyMsg && <span style={{ marginLeft: 12, color: '#64748b' }}>{emptyMsg}</span>}
          <CleanupTable
            rows={emptyItems}
            selectedIds={emptySelected}
            onToggle={toggleEmpty}
            onToggleAll={toggleEmptyAll}
          />
          <button
            type="button"
            style={btnDanger}
            disabled={emptySelected.size === 0}
            onClick={runEmptyDelete}
          >
            선택 항목 삭제 실행
          </button>
        </div>
      </div>

      {/* 2 — 참여기간 불일치 진단 (조회만) */}
      <div style={card}>
        <div style={header}>2. 참여기간·연도 불일치 연차 레코드 진단 (조회 전용)</div>
        <div style={body}>
          <p style={{ margin: '0 0 8px', color: '#475569', fontSize: '0.92rem' }}>
            참여자의 참여 연도·시작·종료일과 맞지 않는 <code>LeaveRecord</code>를 <strong>모두</strong> 나열합니다.
            연차 예산 집계에서 제외되는 행과 동일한 기준입니다. 이 화면에서는 <strong>삭제하지 않습니다</strong>.
            사용·기지급이 비어 있는 행만 지우려면 <strong>1번</strong>을 이용하세요.
          </p>
          <p style={{ margin: '0 0 10px', color: '#b45309', fontSize: '0.88rem' }}>
            연차 사용·기지급이 남아 있는 행(표에서 연한 주황 배경)은 자동 삭제 대상이 아닙니다. 원인을 확인한 뒤 별도로 조치하세요.
          </p>
          <div style={{ marginBottom: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
            <label style={{ fontSize: '0.9rem' }}>
              연도
              <input
                type="text"
                inputMode="numeric"
                placeholder="예: 2025"
                value={outYear}
                onChange={(e) => setOutYear(e.target.value)}
                style={{ marginLeft: 6, padding: '4px 6px', width: 90 }}
              />
            </label>
            <span style={{ fontSize: '0.8rem', color: '#94a3b8' }}>비우면 전체</span>
          </div>
          <button type="button" style={btnPrimary} disabled={outLoading} onClick={loadOutOfRange}>
            {outLoading ? '조회 중…' : '미리보기'}
          </button>
          {outMsg && <span style={{ marginLeft: 12, color: '#64748b' }}>{outMsg}</span>}
          <LeaveOutOfRangeDiagnosticTable rows={outItems} />
        </div>
      </div>

      {/* 3 */}
      <div style={card}>
        <div style={header}>3. 기지급 이상 보정 (활동 중인데 기지급 잔여)</div>
        <div style={body}>
          <p style={{ margin: '0 0 8px', color: '#475569', fontSize: '0.92rem' }}>
            정산 종료가 아닌 참여자인데 해당 연도 <code>LeaveRecord</code>에 기지급이 남아 있는 경우,
            해당 연도 기지급(payoutHours)을 모두 0으로 초기화합니다.
          </p>
          <label>
            대상 연도
            <input
              type="number"
              min={2020}
              max={2100}
              value={payoutYear}
              onChange={(e) => setPayoutYear(parseInt(e.target.value, 10) || new Date().getFullYear())}
              style={{ marginLeft: 8, padding: 6, width: 100 }}
            />
          </label>
          <button type="button" style={btnPrimary} disabled={payoutLoading} onClick={loadPayoutPreview}>
            {payoutLoading ? '조회 중…' : '미리보기'}
          </button>
          {payoutMsg && <span style={{ marginLeft: 12, color: '#64748b' }}>{payoutMsg}</span>}
          {payoutPreview && payoutPreview.items && payoutPreview.items.length > 0 && (
            <div style={{ overflowX: 'auto', marginTop: 12 }}>
              <table style={tableStyle}>
                <thead>
                  <tr>
                    <th style={th}>참여자 ID</th>
                    <th style={th}>성명</th>
                    <th style={th}>사업</th>
                    <th style={th}>기지급 합(시간)</th>
                  </tr>
                </thead>
                <tbody>
                  {payoutPreview.items.map((row) => (
                    <tr key={row.participantId}>
                      <td style={td}>{row.participantId}</td>
                      <td style={td}>{row.name}</td>
                      <td style={td}>{row.projectName}</td>
                      <td style={td}>{row.totalPayoutHours}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div style={{ marginTop: 12 }}>
            <button type="button" style={btnDanger} onClick={runPayoutFix}>
              기지급 0으로 보정 실행
            </button>
          </div>
        </div>
      </div>

      {/* 4 — 취소 건 연차 현황 보정 (기존 연차승인 화면에서 이동) */}
      <div style={card}>
        <div style={header}>4. 취소 건 연차 현황 보정</div>
        <div style={body}>
          <p style={{ margin: '0 0 8px', color: '#475569', fontSize: '0.92rem' }}>
            상태가 <strong>취소</strong>인 연차 신청 중, 해당 사용일이 속한 월의{' '}
            <code>LeaveRecord.usedLeaveDetails</code>에 아직 신청 ID가 남아 있는 건을 찾아 제거합니다.
          </p>
          <div style={{ marginBottom: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
            <label style={{ fontSize: '0.9rem' }}>
              연도
              <input
                type="text"
                inputMode="numeric"
                placeholder="예: 2025"
                value={cancelledYear}
                onChange={(e) => setCancelledYear(e.target.value)}
                style={{ marginLeft: 6, padding: '4px 6px', width: 90 }}
              />
            </label>
            <span style={{ fontSize: '0.8rem', color: '#94a3b8' }}>비우면 전체</span>
          </div>
          <button type="button" style={btnPrimary} disabled={cancelledLoading} onClick={loadCancelledPreview}>
            {cancelledLoading ? '조회 중…' : '미리보기'}
          </button>
          {cancelledMsg && <span style={{ marginLeft: 12, color: '#64748b' }}>{cancelledMsg}</span>}
          {cancelledPreview && !cancelledLoading && cancelledPreview.fixCount > 0 && (
            <div style={{ overflowX: 'auto', marginTop: 12 }}>
              <table style={tableStyle}>
                <thead>
                  <tr>
                    <th style={th}>신청 ID</th>
                    <th style={th}>성명</th>
                    <th style={th}>사업명</th>
                    <th style={th}>사용일자</th>
                    <th style={th}>반영 월</th>
                    <th style={th}>제거 시간(h)</th>
                  </tr>
                </thead>
                <tbody>
                  {(cancelledPreview.items || []).map((row) => (
                    <tr key={`${row.applicationId}-${row.useDate}-${row.leaveRecordYear}-${row.leaveRecordMonth}`}>
                      <td style={td}>{row.applicationId}</td>
                      <td style={td}>{row.participantName}</td>
                      <td style={td}>{row.projectName}</td>
                      <td style={td}>{row.useDate}</td>
                      <td style={td}>
                        {row.leaveRecordYear}년 {row.leaveRecordMonth}월
                      </td>
                      <td style={td}>{Number(row.hours || 0).toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {cancelledPreview && !cancelledLoading && cancelledPreview.fixCount === 0 && (
            <p style={{ marginTop: 12, color: '#64748b' }}>보정할 대상이 없습니다.</p>
          )}
          <div style={{ marginTop: 12 }}>
            <button
              type="button"
              style={btnDanger}
              disabled={
                cancelledLoading ||
                cancelledRunLoading ||
                !cancelledPreview ||
                cancelledPreview.fixCount === 0
              }
              onClick={runCancelledFix}
            >
              {cancelledRunLoading ? '처리 중…' : '보정 실행'}
            </button>
          </div>
        </div>
      </div>

      {/* 5 — 사업단 마감 후 미마감 근무기록 */}
      <div style={card}>
        <div style={header}>5. 사업단 마감 후 미마감 근무기록 강제 마감</div>
        <div style={body}>
          <p style={{ margin: '0 0 8px', color: '#475569', fontSize: '0.92rem' }}>
            같은 사업단·연·월에 <strong>마감된 근무기록이 1건 이상 있는데</strong>, 특정 참여자만 혼자{' '}
            <code>open</code> 상태로 남아 있는 경우입니다.
            미마감 상태이면 연차 예산에서 <strong>만근 예상치</strong>로 잡히므로, 강제 마감하여 실제 데이터를 반영합니다.
            마감 시 총근무시간은 계획 − 결근 − 지각으로 자동 재계산됩니다.
          </p>
          <div style={{ marginBottom: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
            <label style={{ fontSize: '0.9rem' }}>
              연도
              <input
                type="text"
                inputMode="numeric"
                placeholder="예: 2025"
                value={isolatedYear}
                onChange={(e) => setIsolatedYear(e.target.value)}
                style={{ marginLeft: 6, padding: '4px 6px', width: 90 }}
              />
            </label>
            <span style={{ fontSize: '0.8rem', color: '#94a3b8' }}>비우면 전체</span>
          </div>
          <button type="button" style={btnPrimary} disabled={isolatedLoading} onClick={loadIsolatedOpen}>
            {isolatedLoading ? '조회 중…' : '미리보기'}
          </button>
          {isolatedMsg && <span style={{ marginLeft: 12, color: '#64748b' }}>{isolatedMsg}</span>}
          {isolatedItems.length > 0 && (
            <div style={{ overflowX: 'auto', marginTop: 12 }}>
              <table style={tableStyle}>
                <thead>
                  <tr>
                    <th style={{ ...th, width: 40 }}>
                      <input
                        type="checkbox"
                        checked={isolatedItems.length > 0 && isolatedItems.every((r) => isolatedSelected.has(r.id))}
                        onChange={(e) => toggleIsolatedAll(e.target.checked)}
                      />
                    </th>
                    <th style={th}>ID</th>
                    <th style={th}>참여자</th>
                    <th style={th}>연·월</th>
                    <th style={th}>사업</th>
                    <th style={th}>계획(h)</th>
                    <th style={th}>결근(일)</th>
                  </tr>
                </thead>
                <tbody>
                  {isolatedItems.map((r) => (
                    <tr key={r.id}>
                      <td style={td}>
                        <input
                          type="checkbox"
                          checked={isolatedSelected.has(r.id)}
                          onChange={() => toggleIsolated(r.id)}
                        />
                      </td>
                      <td style={td}>{r.id}</td>
                      <td style={td}>{r.participantName}</td>
                      <td style={td}>{r.year}년 {r.month}월</td>
                      <td style={td}>{r.projectName || '-'}</td>
                      <td style={td}>{r.plannedWorkHours}</td>
                      <td style={td}>{r.absenceCount}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {!isolatedLoading && isolatedItems.length === 0 && isolatedMsg && (
            <p style={{ marginTop: 8, color: '#64748b' }}>미마감 항목이 없습니다.</p>
          )}
          <div style={{ marginTop: 12 }}>
            <button
              type="button"
              style={btnPrimary}
              disabled={isolatedSelected.size === 0}
              onClick={runIsolatedClose}
            >
              선택 항목 마감 처리
            </button>
          </div>
        </div>
      </div>

      {/* 6 — 참여기간 밖 근무기록 */}
      <div style={card}>
        <div style={header}>6. 참여기간 밖 근무기록 정리</div>
        <div style={body}>
          <p style={{ margin: '0 0 8px', color: '#475569', fontSize: '0.92rem' }}>
            참여 <strong>시작일·종료일</strong> 기준으로 해당 연·월에 참여하지 않는데 남아 있는{' '}
            <code>WorkRecord</code>입니다. (예: 만근 저장 후 종료일만 앞당긴 경우 등) 근무관리 명단과 동일한
            기간 판정입니다. <strong>마감된 월</strong>도 선택할 수 있으며, 오류가 확실할 때만 삭제하세요
            (실행 시 마감 건이 있으면 추가 확인합니다).
          </p>
          <div style={{ marginBottom: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
            <label style={{ fontSize: '0.9rem' }}>
              연도
              <input
                type="text"
                inputMode="numeric"
                placeholder="예: 2025"
                value={workYear}
                onChange={(e) => setWorkYear(e.target.value)}
                style={{ marginLeft: 6, padding: '4px 6px', width: 90 }}
              />
            </label>
            <span style={{ fontSize: '0.8rem', color: '#94a3b8' }}>비우면 전체</span>
          </div>
          <button type="button" style={btnPrimary} disabled={workLoading} onClick={loadWorkOutOfRange}>
            {workLoading ? '조회 중…' : '미리보기'}
          </button>
          {workMsg && <span style={{ marginLeft: 12, color: '#64748b' }}>{workMsg}</span>}
          <WorkCleanupTable
            rows={workItems}
            selectedIds={workSelected}
            onToggle={toggleWork}
            onToggleAll={toggleWorkAll}
          />
          <button
            type="button"
            style={btnDanger}
            disabled={workSelected.size === 0}
            onClick={runWorkDelete}
          >
            선택 항목 삭제 실행
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminDataCleanupPage;
